import { Controller } from "@hotwired/stimulus"

// Contrôleur pour envoyer le score en temps réel depuis le formulaire standard
export default class extends Controller {
  static targets = [
    "team1Set1", "team2Set1",
    "team1Set2", "team2Set2",
    "team1Set3", "team2Set3"
  ]

  static values = {
    matchId: Number
  }

  connect() {
    console.log('LiveScoreTracker connected for match', this.matchIdValue)

    // Écouter les changements sur tous les champs
    if (this.hasTeam1Set1Target) this.team1Set1Target.addEventListener('input', () => this.scheduleUpdate())
    if (this.hasTeam2Set1Target) this.team2Set1Target.addEventListener('input', () => this.scheduleUpdate())
    if (this.hasTeam1Set2Target) this.team1Set2Target.addEventListener('input', () => this.scheduleUpdate())
    if (this.hasTeam2Set2Target) this.team2Set2Target.addEventListener('input', () => this.scheduleUpdate())
    if (this.hasTeam1Set3Target) this.team1Set3Target.addEventListener('input', () => this.scheduleUpdate())
    if (this.hasTeam2Set3Target) this.team2Set3Target.addEventListener('input', () => this.scheduleUpdate())
  }

  scheduleUpdate() {
    // Debounce: attendre 800ms après le dernier changement
    clearTimeout(this.updateTimeout)
    this.updateTimeout = setTimeout(() => {
      this.sendLiveScore()
    }, 800)
  }

  async sendLiveScore() {
    if (!this.hasMatchIdValue) {
      console.log('No match ID, skipping live score update')
      return
    }

    const liveScore = this.calculateLiveScore()

    // Ne rien envoyer si tous les scores sont à 0
    if (this.allScoresZero(liveScore)) {
      console.log('All scores are 0, skipping update')
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(`/matches/${this.matchIdValue}/live_score`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ live_score: liveScore })
      })

      if (response.ok) {
        console.log('Live score updated successfully', liveScore)
      } else {
        console.error('Failed to update live score:', response.status)
      }
    } catch (error) {
      console.error('Error sending live score:', error)
    }
  }

  calculateLiveScore() {
    const team1_set1 = parseInt(this.team1Set1Target?.value || 0)
    const team2_set1 = parseInt(this.team2Set1Target?.value || 0)
    const team1_set2 = parseInt(this.team1Set2Target?.value || 0)
    const team2_set2 = parseInt(this.team2Set2Target?.value || 0)
    const team1_set3 = parseInt(this.team1Set3Target?.value || 0)
    const team2_set3 = parseInt(this.team2Set3Target?.value || 0)

    // Calculer les sets gagnés
    let sets_won_team1 = 0
    let sets_won_team2 = 0
    let current_set = 1

    // Set 1
    if (team1_set1 > 0 || team2_set1 > 0) {
      if (this.isSetWon(team1_set1, team2_set1)) {
        if (team1_set1 > team2_set1) sets_won_team1++
        else sets_won_team2++
        current_set = 2
      }
    }

    // Set 2
    if (current_set >= 2 && (team1_set2 > 0 || team2_set2 > 0)) {
      if (this.isSetWon(team1_set2, team2_set2)) {
        if (team1_set2 > team2_set2) sets_won_team1++
        else sets_won_team2++
        current_set = 3
      }
    }

    // Set 3
    if (current_set >= 3 && (team1_set3 > 0 || team2_set3 > 0)) {
      if (this.isSetWon(team1_set3, team2_set3)) {
        if (team1_set3 > team2_set3) sets_won_team1++
        else sets_won_team2++
      }
    }

    return {
      current_set,
      team1_set1,
      team2_set1,
      team1_set2,
      team2_set2,
      team1_set3,
      team2_set3,
      sets_won_team1,
      sets_won_team2
    }
  }

  isSetWon(score1, score2) {
    // Un set est gagné si :
    // - Score >= 6 avec écart de 2 (6-4, 7-5, etc.)
    // - Tie-break à 7 points (7-6)
    // - Super tie-break à 10 points (10-8, etc.)

    if (score1 >= 6 && score1 - score2 >= 2) return true
    if (score2 >= 6 && score2 - score1 >= 2) return true
    if (score1 === 7 && score2 === 6) return true
    if (score2 === 7 && score1 === 6) return true
    if (score1 >= 10 && score1 - score2 >= 2) return true
    if (score2 >= 10 && score2 - score1 >= 2) return true

    return false
  }

  allScoresZero(liveScore) {
    return liveScore.team1_set1 === 0 &&
           liveScore.team2_set1 === 0 &&
           liveScore.team1_set2 === 0 &&
           liveScore.team2_set2 === 0 &&
           liveScore.team1_set3 === 0 &&
           liveScore.team2_set3 === 0
  }
}
